export class TokenManager {
    constructor() {
        this.expiryTimer = null;
        this.checkInterval = null;
        this._storageHandler = null;
        this._visibilityHandler = null;
        this.initializeTokenChecking();
    }

    _getDashboardPath(role, isAdmin) {
        if (role === 'student') return '/StudentDashboard';
        if (role === 'super_admin') return '/SuperAdminDashboard';
        if (role === 'admin') return '/standaloneAdminDashboard';
        if (role === 'preceptor') return isAdmin === 'true' ? '/AdminDashboard' : '/PreceptorDashboard';
        return null;
    }

    _handleStorageEvent(event) {
        // Another tab finished logging in — token_expiry is set last by setToken
        if (event.key === 'token_expiry' && event.newValue) {
            if (window.location.pathname === '/') {
                const dashboard = this._getDashboardPath(
                    localStorage.getItem('role'),
                    localStorage.getItem('isAdmin')
                );
                if (dashboard) window.location.href = dashboard;
            }
            return;
        }

        // Another tab logged out — access_token was removed
        if (event.key === 'access_token' && !event.newValue) {
            const publicPaths = ['/', '/forgot-password', '/reset-password', '/change-password'];
            const onPublic = publicPaths.some((p) => window.location.pathname === p);
            if (!onPublic) window.location.href = '/';
        }
    }

    initializeTokenChecking() {
        // On startup: silently clear an already-expired token. PrivateRoute
        // handles the redirect synchronously, so no notification is needed here.
        const token = localStorage.getItem('access_token');
        const expiryString = localStorage.getItem('token_expiry');
        if (token && expiryString && new Date() >= new Date(expiryString)) {
            this.clearStorage();
        }

        // Periodic fallback check every minute
        this.checkInterval = setInterval(() => this.checkTokenExpiry(), 60000);

        // Cross-tab sync
        this._storageHandler = (e) => this._handleStorageEvent(e);
        window.addEventListener('storage', this._storageHandler);

        // Immediate check when the user returns to the tab (browser throttles
        // timers in background, so we can't rely on setTimeout alone).
        this._visibilityHandler = () => {
            if (document.visibilityState === 'visible') this.checkTokenExpiry();
        };
        document.addEventListener('visibilitychange', this._visibilityHandler);
    }

    setToken(token, expiryMinutes = 500) {
        const expiryTime = new Date(Date.now() + expiryMinutes * 60 * 1000);
        localStorage.setItem('access_token', token);
        localStorage.setItem('token_expiry', expiryTime.toISOString());
        localStorage.setItem('token_expiry_minutes', expiryMinutes.toString());
        this.setupExpiryHandling(expiryMinutes * 60 * 1000);
    }

    startMonitoring() {
        const token = localStorage.getItem('access_token');
        const expiryString = localStorage.getItem('token_expiry');
        if (!token || !expiryString) return;

        const remainingMs = new Date(expiryString).getTime() - Date.now();
        if (remainingMs <= 0) {
            this.clearStorage();
            return;
        }

        this.setupExpiryHandling(remainingMs);
    }

    setupExpiryHandling(msUntilExpiry) {
        if (this.expiryTimer) clearTimeout(this.expiryTimer);
        this.expiryTimer = setTimeout(() => this.handleTokenExpiry(), msUntilExpiry);
    }

    checkTokenExpiry() {
        const token = localStorage.getItem('access_token');
        const expiryString = localStorage.getItem('token_expiry');
        if (!token || !expiryString) return false;

        if (Date.now() >= new Date(expiryString).getTime()) {
            this.handleTokenExpiry();
            return true;
        }
        return false;
    }

    handleTokenExpiry() {
        if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('🔴 Token expired — redirecting to login');
        this.clearTimers();
        this.clearStorage();
        window.location.href = '/';
    }

    clearTimers() {
        if (this.expiryTimer) { clearTimeout(this.expiryTimer); this.expiryTimer = null; }
        if (this.checkInterval) { clearInterval(this.checkInterval); this.checkInterval = null; }
    }

    clearStorage() {
        [
            'access_token', 'token_expiry', 'token_expiry_minutes',
            'admin_token', 'user_id', 'student_id', 'preceptor_id', 'admin_id',
            'user_type', 'userType', 'role', 'isAdmin', 'student_status',
            'impersonation_session',
        ].forEach(k => localStorage.removeItem(k));
    }

    destroy() {
        this.clearTimers();
        if (this._storageHandler) {
            window.removeEventListener('storage', this._storageHandler);
            this._storageHandler = null;
        }
        if (this._visibilityHandler) {
            document.removeEventListener('visibilitychange', this._visibilityHandler);
            this._visibilityHandler = null;
        }
    }

    getTimeRemaining() {
        const expiryString = localStorage.getItem('token_expiry');
        if (!expiryString) return 0;
        return Math.max(0, Math.floor((new Date(expiryString).getTime() - Date.now()) / 60000));
    }
}

export const tokenManager = new TokenManager();

export const initializeTokenManager = () => tokenManager;
export const setTokenWithExpiry = (token, expiryMinutes) => tokenManager.setToken(token, expiryMinutes);
export const checkTokenExpiry = () => tokenManager.checkTokenExpiry();
export const getTimeRemaining = () => tokenManager.getTimeRemaining();
export const clearTokenManager = () => tokenManager.destroy();
